/**
 * DashboardSettingsContext
 * 
 * Provides centralized dashboard settings including:
 * - Date range for all reporting components
 * - Projection toggle state
 * - Settings panel visibility state
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

export interface DashboardSettingsState {
  dateRange: DateRange;
  showProjections: boolean;
  isSettingsPanelOpen: boolean;
}

export interface DashboardSettingsActions {
  setDateRange: (range: DateRange) => void;
  setShowProjections: (show: boolean) => void;
  toggleProjections: () => void;
  openSettingsPanel: () => void;
  closeSettingsPanel: () => void;
  toggleSettingsPanel: () => void;
  resetSettings: () => void;
}

export interface DashboardSettingsContextType 
  extends DashboardSettingsState, 
          DashboardSettingsActions {}

// ============================================================================
// DEFAULTS
// ============================================================================

const DEFAULT_DATE_RANGE: DateRange = {
  startDate: null,
  endDate: null,
};

const DEFAULT_STATE: DashboardSettingsState = {
  dateRange: DEFAULT_DATE_RANGE,
  showProjections: false,
  isSettingsPanelOpen: false,
};

// ============================================================================
// CONTEXT
// ============================================================================

const DashboardSettingsContext = createContext<DashboardSettingsContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

interface DashboardSettingsProviderProps {
  children: ReactNode;
  initialDateRange?: DateRange;
  initialShowProjections?: boolean;
}

export const DashboardSettingsProvider: React.FC<DashboardSettingsProviderProps> = ({
  children,
  initialDateRange,
  initialShowProjections,
}) => {
  const [state, setState] = useState<DashboardSettingsState>({
    dateRange: initialDateRange || DEFAULT_DATE_RANGE,
    showProjections: initialShowProjections ?? false,
    isSettingsPanelOpen: false,
  });

  // Date range actions
  const setDateRange = useCallback((range: DateRange) => {
    setState(prev => ({ ...prev, dateRange: range }));
  }, []);

  // Projections actions
  const setShowProjections = useCallback((show: boolean) => {
    setState(prev => ({ ...prev, showProjections: show }));
  }, []);

  const toggleProjections = useCallback(() => {
    setState(prev => ({ ...prev, showProjections: !prev.showProjections }));
  }, []);

  // Settings panel actions
  const openSettingsPanel = useCallback(() => {
    setState(prev => ({ ...prev, isSettingsPanelOpen: true }));
  }, []);

  const closeSettingsPanel = useCallback(() => {
    setState(prev => ({ ...prev, isSettingsPanelOpen: false }));
  }, []);

  const toggleSettingsPanel = useCallback(() => {
    setState(prev => ({ ...prev, isSettingsPanelOpen: !prev.isSettingsPanelOpen }));
  }, []);

  // Reset
  const resetSettings = useCallback(() => {
    setState(DEFAULT_STATE);
  }, []);

  const contextValue: DashboardSettingsContextType = {
    ...state,
    setDateRange,
    setShowProjections,
    toggleProjections,
    openSettingsPanel,
    closeSettingsPanel,
    toggleSettingsPanel,
    resetSettings,
  };

  return (
    <DashboardSettingsContext.Provider value={contextValue}>
      {children}
    </DashboardSettingsContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export const useDashboardSettings = (): DashboardSettingsContextType => {
  const context = useContext(DashboardSettingsContext);
  if (!context) {
    throw new Error('useDashboardSettings must be used within a DashboardSettingsProvider');
  }
  return context;
};

// ============================================================================
// OPTIONAL HOOK (doesn't throw if outside provider)
// ============================================================================

export const useDashboardSettingsOptional = (): DashboardSettingsContextType | null => {
  const context = useContext(DashboardSettingsContext);
  return context ?? null;
};

export default DashboardSettingsContext;
